import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QuickAddCard, TaskDropdownBelowTrigger, dropdownLayerOpen, useQuickAddSession, useReminderPlan } from '../../features/taskManagement'
import type { QuickAddCreated, TaskEntryActions } from '../../features/taskManagement'
import type { LifeSpace } from '../../lib/db/models'
import { useTransparentBackdrop } from '../../hooks/useTransparentBackdrop'
import { getOpenProjectId } from '../../lib/openProject'
import { initAnalytics, trackPageview } from '../../lib/analytics'
import { setAppearanceOverride } from '../../lib/theme/appearanceOverride'
import { useQuickAddWindowHeight } from './useQuickAddWindowHeight'
import { useQuickAddGlass } from './useQuickAddGlass'
import './QuickAddWindow.scss'

const LIFE_SPACES: readonly LifeSpace[] = ['personal', 'work', 'home']

/** Der Bereich, den das Hauptfenster gerade zeigt. „Alle“ ist kein Bereich — dann Persönlich. */
export function activeLifeSpace(): LifeSpace {
  try {
    const stored = localStorage.getItem('activeLifeSpace')
    return LIFE_SPACES.find(space => space === stored) ?? 'personal'
  } catch {
    return 'personal'
  }
}

/**
 * **Der Griff zum Verschieben** (Sven, 04.10.2026): der Streifen über dem Titel. Er meldet den Zeiger in
 * Bildschirm-Koordinaten; der Hauptprozess zieht Karte und Glas gemeinsam (`dragQuickAdd`). Kein
 * `-webkit-app-region: drag` — dessen Bewegung kommt nicht laufend an, das Glas hinge hinterher.
 *
 * Der Platz wird gemerkt; ein Doppelklick holt sie zurück ins obere Drittel (#275).
 */
const QuickAddGrip: React.FC = () => {
  const dragging = useRef(false)
  const send = (phase: 'start' | 'move' | 'end', e: React.PointerEvent) => {
    window.windowAPI?.dragQuickAdd?.(phase, e.screenX, e.screenY)
  }
  const end = (e: React.PointerEvent) => {
    if (!dragging.current) return
    dragging.current = false
    send('end', e)
  }
  return (
    <div
      className="quick-add-window__grip"
      aria-hidden="true"
      onPointerDown={(e) => {
        if (e.button !== 0) return
        // Der Caret bleibt im Titel — wer verschiebt, will danach weiterschreiben.
        e.preventDefault()
        dragging.current = true
        e.currentTarget.setPointerCapture?.(e.pointerId)
        send('start', e)
      }}
      onPointerMove={(e) => { if (dragging.current) send('move', e) }}
      onPointerUp={end}
      onPointerCancel={end}
      onLostPointerCapture={end}
      onDoubleClick={(e) => {
        if (e.button === 0) window.windowAPI?.resetQuickAddPosition?.()
      }}
    />
  )
}

/** Hält den Plan der Erinnerungen aktuell, solange kein Hauptfenster das tut. */
const ReminderPlan: React.FC = () => {
  useReminderPlan()
  return null
}

/**
 * **Die Schnelleingabe als eigenes Fenster** (#234) — die Desktop-Hülle um `QuickAddCard`. Im Web
 * ist es ein Dialog (`QuickAddDialog`); was beide gleich tun, steht in `useQuickAddSession`.
 *
 * Das Fenster wird nie geschlossen, nur versteckt (F3): Wer daneben klickt, findet seinen Entwurf
 * beim nächsten Öffnen wieder. Der Hauptprozess meldet jedes Zeigen (`quick-add:shown`).
 */
export const QuickAddWindow: React.FC = () => {
  const { t } = useTranslation()
  const actionsRef = useRef<TaskEntryActions | null>(null)
  const cardRef = useRef<HTMLDivElement>(null)

  const hide = useCallback(() => { void window.windowAPI?.hideQuickAdd?.() }, [])
  // Diese Karte fährt keinen eigenen Sync — das Hauptfenster schiebt die Aufgabe hoch und sagt in einem
  // Toast, wo sie liegt (`useQuickAddCreatedToast`). Nach ⇧↵ holt der Hauptprozess das Hauptfenster nach
  // vorn, und es springt zur Aufgabe (`useQuickAddReveal`); dann ohne Toast.
  const announce = useCallback(({ task, lifeSpace, details, reveal }: QuickAddCreated) => {
    void window.windowAPI?.broadcast?.('quick-add:created', { lifeSpace, taskId: task.id, details, reveal })
    if (reveal) void window.windowAPI?.revealQuickAddTask?.(task.id)
  }, [])
  const { lifeSpace, setLifeSpace, preferredProjectId, resetKey, notice, begin, noteDraft, save, discard } = useQuickAddSession({
    startLifeSpace: activeLifeSpace,
    startProjectId: getOpenProjectId,
    onClose: hide,
    onCreated: announce,
  })

  useTransparentBackdrop()
  useQuickAddWindowHeight(cardRef)
  // Auf dem Mac liegt echtes Glas hinter der Karte (`createQuickAddGlass`); der Hauptprozess sagt es in der Adresse.
  const [glass] = useState(() => /[?&]glass=1(?:&|$)/.test(window.location.hash))
  useQuickAddGlass(cardRef, glass)

  useEffect(() => {
    document.title = t('quickAdd.title')
  }, [t])

  // Ohne Hauptfenster (macOS, alle zu) plant niemand sonst die Erinnerung einer hier angelegten Aufgabe.
  const [alone, setAlone] = useState(false)
  useEffect(() => window.windowAPI?.onWindowMessage?.('quick-add:shown', (data) => {
    // Vorgewärmt lädt das Fenster beim App-Start — gesehen ist es erst jetzt. Darum startet PostHog
    // hier (`analyticsWaitsForShow`) und der Pageview kommt hier (`routeSendsOwnPageview`).
    initAnalytics()
    trackPageview('/quick-add')
    setAlone((data as { alone?: boolean } | undefined)?.alone === true)
    begin()
    actionsRef.current?.focusTitle()
  }), [begin])

  // Hell oder dunkel wie das System, nicht wie die App (Sven, 04.10.2026) — jeder Wechsel kommt vom
  // Hauptprozess, auch versteckt; den Anfang legt das Kopfskript aus der Adresse.
  useEffect(() => window.windowAPI?.onWindowMessage?.('quick-add:appearance', (data) => {
    setAppearanceOverride((data as { dark?: boolean } | undefined)?.dark ? 'dark' : 'light')
  }), [])

  // Esc, während der Fokus auf einem Knopf liegt. In den Feldern fängt die Titelzeile es selbst ab,
  // in einem offenen Kasten schließt es nur den Kasten.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || dropdownLayerOpen()) return
      e.preventDefault()
      discard()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [discard])

  return (
    <div className={`quick-add-window${glass ? ' quick-add-window--glass' : ''}`}>
      {alone && <ReminderPlan />}
      <div ref={cardRef} className="quick-add-window__card">
        <QuickAddGrip />
        {/* Die Kästen gehen nach unten auf — das Fenster wächst ihnen nach (`useQuickAddWindowHeight`). */}
        <TaskDropdownBelowTrigger.Provider value>
          <QuickAddCard
            lifeSpace={lifeSpace}
            onLifeSpaceChange={setLifeSpace}
            onSubmit={(data) => void save(data)}
            onSubmitAndContinue={(data) => void save(data, 'keepOpen')}
            onSubmitAndReveal={(data) => void save(data, 'reveal')}
            // Ohne Titel gibt es nichts anzulegen — das Fenster wackelt wie das Fokus-Fenster, und der Caret
            // steht im Titel, auch nach einem Klick auf „Anlegen“ (Sven, 04.10.2026).
            onSubmitEmpty={() => {
              actionsRef.current?.focusTitle()
              void window.windowAPI?.wiggleQuickAdd?.()
            }}
            onCancel={discard}
            resetKey={resetKey}
            notice={notice}
            actionsRef={actionsRef}
            preferredProjectId={preferredProjectId}
            onDraftChange={noteDraft}
          />
        </TaskDropdownBelowTrigger.Provider>
      </div>
    </div>
  )
}
